import React, { useMemo } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { cn } from '../utils/cn';

const { FiList, FiInbox, FiExternalLink, FiCpu, FiUser } = FiIcons;

const fmtPrice = (p) => {
  if (p == null || !isFinite(p)) return '—';
  if (p < 0.00001) return `$${p.toFixed(8)}`;
  if (p < 0.01) return `$${p.toFixed(6)}`;
  return `$${p.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
};
const fmtTs = (v) => { try { return v ? new Date(v).toLocaleString() : '—'; } catch { return '—'; } };

// Labeled field for the secondary (detail) row of each trade.
const Detail = ({ label, children }) => (
  <span className="inline-flex items-baseline gap-1 text-[10px]">
    <span className="text-slate-600 uppercase font-bold text-[9px]">{label}</span>
    <span className="font-mono text-slate-300">{children}</span>
  </span>
);

export default function TradesList({ positions }) {
  const trades = useMemo(() => {
    return (positions || [])
      .filter((p) => p.status === 'CLOSED')
      .map((p) => {
        const entry = p.entryPrice;
        const exit = p.exitPrice != null ? p.exitPrice : p.currentPrice;
        const cost = p.costSol != null ? p.costSol : p.amount;
        const hasRealized = p.realizedSol != null && cost != null && cost > 0;
        const pnlSol = hasRealized
          ? p.realizedSol - cost
          : (p.amount && entry ? p.amount * (exit / entry) - p.amount : 0);
        // Prefer the true return on capital (realized SOL vs cost) — immune to price-feed
        // glitches that can corrupt entry_price. Fall back to the price ratio only when there's
        // no realized data (older/PAPER rows).
        const pnlPct = hasRealized
          ? (pnlSol / cost) * 100
          : (entry ? ((exit - entry) / entry) * 100 : 0);
        return { ...p, _entry: entry, _exit: exit, _pnlSol: pnlSol, _pnlPct: pnlPct };
      })
      .sort((a, b) => new Date(b.closedAt || b.updatedAt || b.timestamp) - new Date(a.closedAt || a.updatedAt || a.timestamp));
  }, [positions]);

  const pnlColor = (v) => (v > 0 ? 'text-green-400' : v < 0 ? 'text-red-400' : 'text-slate-300');

  return (
    <div className="h-full overflow-y-auto custom-scrollbar pr-2">
      <div className="max-w-7xl mx-auto space-y-6 pb-12">
        <div className="flex items-center gap-2">
          <SafeIcon icon={FiList} className="text-purple-400 text-2xl" />
          <h2 className="text-xl font-bold text-white">Trades List</h2>
          <span className="ml-auto bg-slate-800 px-3 py-1 rounded-full text-[10px] font-bold text-slate-300 uppercase tracking-wider">
            {trades.length} completed
          </span>
        </div>

        <div className="glass-panel rounded-2xl p-4 sm:p-6 overflow-x-auto custom-scrollbar">
          {trades.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-slate-600 py-20">
              <SafeIcon icon={FiInbox} className="text-5xl opacity-10 mb-4" />
              <p className="text-sm font-medium">No completed trades yet.</p>
              <p className="text-xs">Closed trades will appear here with full details.</p>
            </div>
          ) : (
            <table className="w-full text-sm border-collapse min-w-[680px]">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
                  <th className="text-left font-bold py-2 px-2">Token</th>
                  <th className="text-left font-bold py-2 px-2">Origin</th>
                  <th className="text-left font-bold py-2 px-2">Strategy</th>
                  <th className="text-right font-bold py-2 px-2">Entry</th>
                  <th className="text-right font-bold py-2 px-2">Exit</th>
                  <th className="text-right font-bold py-2 px-2">PnL</th>
                  <th className="text-left font-bold py-2 px-2">Closed</th>
                </tr>
              </thead>
              <tbody>
                {trades.map((t) => (
                  <React.Fragment key={t.id}>
                    {/* Row 1 — headline */}
                    <tr className="hover:bg-slate-800/20">
                      <td className="pt-3 px-2 align-top">
                        <div className="flex items-center gap-1.5">
                          <span className="font-bold text-white">{t.tokenSymbol || 'Unknown'}</span>
                          {t.dexUrl && (
                            <a href={t.dexUrl} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">
                              <SafeIcon icon={FiExternalLink} className="text-xs" />
                            </a>
                          )}
                        </div>
                        <div className="font-mono text-[9px] text-slate-500">{t.tokenAddress?.slice(0, 4)}…{t.tokenAddress?.slice(-4)}</div>
                      </td>
                      <td className="pt-3 px-2 align-top">
                        <span className={cn('inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded font-bold uppercase border',
                          t.botTriggered ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' : 'bg-slate-800 text-slate-400 border-slate-700')}>
                          <SafeIcon icon={t.botTriggered ? FiCpu : FiUser} />
                          {t.botTriggered ? 'Auto' : 'Manual'}
                        </span>
                        <div className="text-[9px] text-slate-500 font-mono mt-0.5">{t.timeframe || '—'}</div>
                      </td>
                      <td className="pt-3 px-2 align-top text-[11px] text-slate-300 capitalize">{t.strategy || '—'}</td>
                      <td className="pt-3 px-2 align-top text-right font-mono text-slate-300">{fmtPrice(t._entry)}</td>
                      <td className="pt-3 px-2 align-top text-right font-mono text-slate-300">{fmtPrice(t._exit)}</td>
                      <td className={cn('pt-3 px-2 align-top text-right font-mono font-bold', pnlColor(t._pnlSol))}>
                        <div>{t._pnlSol > 0 ? '+' : ''}{t._pnlSol.toFixed(4)} SOL</div>
                        <div className="text-[9px] font-medium">{t._pnlPct > 0 ? '+' : ''}{t._pnlPct.toFixed(2)}%</div>
                      </td>
                      <td className="pt-3 px-2 align-top text-[10px] text-slate-400 whitespace-nowrap">{fmtTs(t.closedAt || t.updatedAt)}</td>
                    </tr>
                    {/* Row 2 — detail strip */}
                    <tr className="border-b border-slate-800/50 hover:bg-slate-800/20">
                      <td colSpan={7} className="pb-3 px-2">
                        <div className="flex flex-wrap gap-x-4 gap-y-1 items-baseline">
                          <span className="inline-flex items-baseline gap-1">
                            <span className="text-slate-600 uppercase font-bold text-[9px]">Mode</span>
                            <span className={cn('text-[9px] px-1.5 py-0.5 rounded font-bold uppercase border',
                              t.mode === 'LIVE' ? 'bg-red-500/10 text-red-400 border-red-500/20' : 'bg-slate-800 text-slate-400 border-slate-700')}>{t.mode}</span>
                          </span>
                          <Detail label="Hard Stop"><span className="text-red-400/80">{t.hardStop != null ? `${fmtPrice(t._entry * (1 - t.hardStop / 100))} (-${parseFloat(t.hardStop.toFixed(1))}%)` : '—'}</span></Detail>
                          <Detail label="Take Profit"><span className="text-green-400/80">{t.takeProfit != null ? `${fmtPrice(t._entry * (1 + t.takeProfit / 100))} (+${parseFloat(t.takeProfit.toFixed(1))}%)` : '—'}</span></Detail>
                          <Detail label="Trailing"><span className="text-orange-400/80">{t.trailingStop != null && t.highestPrice != null ? `${fmtPrice(t.highestPrice * (1 - t.trailingStop / 100))} (-${parseFloat(t.trailingStop.toFixed(1))}%)` : '—'}</span></Detail>
                          <Detail label="Peak"><span className="text-emerald-300/90">{t.highestPrice != null ? `${fmtPrice(t.highestPrice)}${t._entry ? ` (+${parseFloat((((t.highestPrice / t._entry) - 1) * 100).toFixed(1))}%)` : ''}` : '—'}</span></Detail>
                          <Detail label="Priority">{t.priorityFee != null ? `${t.priorityFee} SOL` : '—'}</Detail>
                          <Detail label="Slippage">{t.slippageBps != null ? `${t.slippageBps} bps` : '—'}</Detail>
                          <Detail label="Bought">{fmtTs(t.boughtAt || t.timestamp)}</Detail>
                          <Detail label="Reason"><span className="text-orange-400/90">{t.sellReason || '—'}</span></Detail>
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
